import { themeTextColors, ZeroStateModules } from "@common/constants";
import cx from "classnames";
import { FC, useMemo } from "react";
import { HiOutlineCheckCircle, HiOutlineLockClosed } from "react-icons/hi";
import useStore from "@common/state";
import { Transition } from "@headlessui/react";
import { Button, Typography } from "@earned/core";
import { useIsCobranded } from "@common/hooks";
import { ADVISOR_SUPPORT_EMAIL } from "@modules/advisory-team/constants";

interface ZeroStateProps {
  name: ZeroStateModules;
  embedded?: boolean;
  alwaysShowLock?: boolean;
}

const ZeroState: FC<ZeroStateProps> = ({ name, embedded, alwaysShowLock }) => {
  const { setIsAdvisorChatOpen } = useStore();
  const { isCobranded } = useIsCobranded();
  const title = useMemo(() => {
    if (isCobranded) {
      return "Get a holistic view of your wealth and financial health";
    }
    switch (name) {
      case ZeroStateModules.CobrandedExperience:
      case ZeroStateModules.ThreeSixtyView:
        return "Get a holistic view of your wealth and financial health";
      case ZeroStateModules.FinancialPlan:
        return "Get a detailed financial plan that sets up your financial future";
      case ZeroStateModules.Investments:
        return "Get personalized tax-smart investing to meet your financial goals";
    }
  }, [name, isCobranded]);
  const features = useMemo(() => {
    if (name === ZeroStateModules.CobrandedExperience || isCobranded) {
      return [
        "Visualize wealth projections over time",
        "Holistic advice across all your financial needs",
        "Tax-smart investing and tax efficient portfolios",
      ];
    }
    switch (name) {
      case ZeroStateModules.ThreeSixtyView:
        return [
          "See your Net Worth dynamically updated",
          "Visualize wealth projections over time",
          "Holistic advice across all your financial needs",
        ];
      case ZeroStateModules.FinancialPlan:
        return [
          "Visualize wealth projections over time",
          "Run scenarios to project impact of decisions",
          "Holistic advice across all your financial needs",
        ];
      case ZeroStateModules.Investments:
        return [
          "Tax-smart investing and tax efficient portfolios",
          "Tailored for physicians and personalized to you",
          "Institutional-caliber private capital investments",
        ];
      default:
        return [];
    }
  }, [name, isCobranded]);

  return (
    <div className="flex flex-1 flex-col items-center gap-8 font-medium">
      <div
        className={
          "absolute bottom-0 left-0 right-0 z-[11] flex h-full w-full flex-1 flex-col items-center md:items-center " +
          (!embedded ? "grid" : "")
        }
      >
        <div className="flex flex-col items-center gap-8">
          <div
            className={
              "h-15 w-15 flex-none " +
              (embedded && !alwaysShowLock ? "hidden" : "")
            }
          >
            <div className="flex h-15 w-15 flex-row items-center justify-center rounded-full bg-green-5 shadow-md">
              <HiOutlineLockClosed size={24} color="white" />
            </div>
          </div>
          <div
            className={cx(
              "flex max-w-[519px] flex-none pb-0 text-center",
              embedded ? "px-0" : "px-10"
            )}
          >
            <Typography variant="title-lg" color="primary">
              {title}
            </Typography>
          </div>
          <div
            className={cx(
              "flex flex-none flex-col items-center gap-2",
              embedded ? "px-0" : "px-10"
            )}
          >
            {features.map((feature) => (
              <div key={feature} className="flex flex-row items-center gap-2">
                <HiOutlineCheckCircle
                  color={themeTextColors.primary}
                  size={24}
                />
                <Typography variant="body" color="secondary">
                  {feature}
                </Typography>
              </div>
            ))}
          </div>
          {!embedded && (
            <Button variant="primary" href={`mailto:${ADVISOR_SUPPORT_EMAIL}`}>
              Talk to an Advisor
            </Button>
          )}
        </div>
      </div>
      <Transition
        show={true}
        enter="transition-opacity duration-75"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="transition-opacity duration-150"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div
          className={
            "absolute bottom-0 left-0 right-0 top-0 flex h-full w-full flex-1 flex-row items-end justify-center bg-[rgba(255,255,255,0.89)] " +
            (embedded ? "z-[2]" : "z-[10]")
          }
        />
      </Transition>
    </div>
  );
};

export default ZeroState;
